import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { buildKeys } from "../query-keys";
import { asEventId, type BuildId, type EventId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

export { buildKeys } from "../query-keys";

type BuildCreate = components["schemas"]["BuildCreate"];

export function useBuildsByEvent(eventId: EventId) {
  return useQuery({
    queryKey: buildKeys.byEvent(eventId),
    queryFn: async () => {
      const result = await api.GET("/api/builds/event/{event_id}", {
        params: { path: { event_id: eventId } },
      });
      return throwIfError(result, "Failed to fetch builds");
    },
    enabled: !!eventId,
  });
}

export function useBuild(buildId: BuildId) {
  return useQuery({
    queryKey: buildKeys.detail(buildId),
    queryFn: async () => {
      const result = await api.GET("/api/builds/{build_id}", {
        params: { path: { build_id: buildId } },
      });
      return throwIfError(result, "Failed to fetch build");
    },
    enabled: !!buildId,
  });
}

export function useSignOffBuild(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (signedBy: string) => {
      const result = await api.POST("/api/builds/{build_id}/sign-off", {
        params: { path: { build_id: buildId } },
        body: { signed_by: signedBy },
      });
      return throwIfError(result, "Failed to sign off build");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}

export function useUnlockBuild(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await api.POST("/api/builds/{build_id}/unlock", {
        params: { path: { build_id: buildId } },
      });
      return throwIfError(result, "Failed to unlock build");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}

export function useCreateBuild() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BuildCreate) => {
      const result = await api.POST("/api/builds", { body });
      return throwIfError(result, "Failed to create build");
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: buildKeys.byEvent(asEventId(vars.event_id)),
      });
    },
  });
}

export function useUpdateBuildNotes(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (notes: string | null) => {
      const result = await api.PATCH("/api/builds/{build_id}/notes", {
        params: { path: { build_id: buildId } },
        body: { notes },
      });
      return throwIfError(result, "Failed to update notes");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
    },
  });
}
